import { GetAuthorReviewsQuery } from "@omni/types/api/reviews/bff/request/authorReviews.query";
import { Review } from "@omni/types/api/reviews/bff/response/review.model";
import { ReviewResponse } from "@omni/types/api/reviews/bff/response/reviews.response";
import { isEmpty } from "lodash-es";
import { useCallback, useEffect, useState } from "react";

import { useBFFProductServiceApi } from "@/api/hooks/productService.hook";
import { ReviewsMapperBFF } from "@/api/mappers/review.mapper";
import { PAGE_SIZE } from "@/components/Product/Reviews/reviews.config";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { ReviewAuthorModel } from "@/types/service/reviews/reviewAuthor.model";

import { ReviewerModalComponent } from "./reviewerModal.component";

type ReviewerModalProps = {
    range: number;
    reviewer: ReviewAuthorModel;
    open: boolean;
    toggleHandler: (open: boolean) => void;
};

export const ReviewerModal = ({
    range,
    reviewer,
    open,
    toggleHandler
}: ReviewerModalProps) => {
    const { t } = useGranite();
    const [reviews, setReviews] = useState<Array<Review>>([]);
    const [totalResults, setTotalResults] = useState<number>(0);

    const [{ loading }, fetchReviews] = useBFFProductServiceApi<
        undefined,
        ReviewResponse,
        ReviewResponse
    >(
        {
            url: "/reviews/author",
            method: "GET",
            mapper: (model) => ReviewsMapperBFF(model)(t)
        },
        {
            manual: true,
            useCache: false
        }
    );

    const getReviews = useCallback(
        async (partialRequest: Partial<GetAuthorReviewsQuery>) => {
            const request: GetAuthorReviewsQuery = {
                authorId: reviewer.id,
                offset: 0,
                limit: PAGE_SIZE,
                ...partialRequest
            };

            const response = await fetchReviews({
                params: request
            });

            return response.data;
        },
        [reviewer.id, fetchReviews]
    );

    const loadMore = async () => {
        const response = await getReviews({
            offset: reviews.length
        });

        setReviews((prev) => [...prev, ...response.reviews]);
    };

    useEffect(() => {
        if (!open) return;
        if (!isEmpty(reviews)) return;

        getReviews({ offset: 0 }).then((data) => {
            setTotalResults(data.totalResults);
            setReviews(data.reviews);
        });
    }, [open, reviews, getReviews]);

    return (
        <ReviewerModalComponent
            range={range}
            reviewer={reviewer}
            open={open}
            reviews={reviews}
            totalReviews={totalResults}
            loading={loading}
            loadMore={loadMore}
            toggleHandler={toggleHandler}
        />
    );
};
